import axios from 'axios';
import authService from './authService';
import router from '@/router';
import { API_URL } from '@/config/api';

const api = axios.create({
  baseURL: API_URL,
  timeout: 30000,
});

// Interceptor para adicionar token em todas as requisições
api.interceptors.request.use(
  (config) => {
    const token = authService.token.get();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para tratamento de erros
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Só faz logout se a rota atual requer autenticação
      const currentRoute = router.currentRoute;
      const requiresAuth = currentRoute.matched.some(record => record.meta.requiresAuth === true);

      if (requiresAuth) {
        authService.logout();
      }
    }
    return Promise.reject(error);
  }
);

export default {
  /**
   * Listar todos os presentes ativos
   * @returns {Promise<Array>} Lista de presentes ativos com informações de reservas e pagamentos
   * @returns {number} returns[].id - ID único do presente
   * @returns {string} returns[].name - Nome do presente
   * @returns {string} returns[].description - Descrição detalhada do presente
   * @returns {number} returns[].suggestedValue - Valor sugerido para o presente
   * @returns {string} returns[].imageUrl - URL da imagem do presente (opcional)
   * @returns {boolean} returns[].active - Indica se o presente está ativo
   * @returns {boolean} returns[].reserved - Indica se o presente está reservado
   * @returns {object|null} returns[].reservedBy - Informações do convidado que reservou
   * @returns {string|null} returns[].reservedAt - Data e hora da reserva
   * @returns {boolean} returns[].purchased - Indica se o presente já foi comprado
   * @returns {object|null} returns[].payment - Informações do pagamento associado
   */
  getAllGifts() {
    return api.get('/gifts');
  },

  /**
   * Listar todos os itens do chá de panela
   */
  getChaDePanelaGifts() {
    return api.get('/gifts/by-event/CHA_DE_PANELA');
  },

  /**
   * Listar itens por tipo de evento
   * @param {string} eventType - 'CASAMENTO' | 'CHA_DE_PANELA'
   */
  getGiftsByEvent(eventType) {
    return api.get(`/gifts/by-event/${eventType}`);
  },

  /**
   * Buscar presente por ID
   * @param {number} id - ID do presente
   * @returns {Promise<object>} Dados do presente
   * @returns {number} returns.id - ID único do presente
   * @returns {string} returns.name - Nome do presente
   * @returns {string} returns.description - Descrição detalhada do presente
   * @returns {number} returns.suggestedValue - Valor sugerido para o presente
   * @returns {string} returns.imageUrl - URL da imagem do presente (opcional)
   * @returns {boolean} returns.active - Indica se o presente está ativo
   * @returns {boolean} returns.reserved - Indica se o presente está reservado
   * @returns {object|null} returns.reservedBy - Informações do convidado que reservou
   * @returns {string|null} returns.reservedAt - Data e hora da reserva
   * @returns {boolean} returns.purchased - Indica se o presente já foi comprado
   * @returns {string} returns.createdAt - Data e hora de criação do presente
   */
  getGiftById(id) {
    return api.get(`/gifts/${id}`);
  },

  /**
   * Criar novo presente
   * @param {object} giftData - Dados do presente
   * @param {string} giftData.name - Nome do presente (máximo 100 caracteres, obrigatório)
   * @param {string} giftData.description - Descrição detalhada do presente (obrigatório)
   * @param {number} giftData.suggestedValue - Valor sugerido para o presente (obrigatório)
   * @param {string} [giftData.imageUrl] - URL da imagem do presente (opcional)
   * @param {boolean} [giftData.active=true] - Indica se o presente está ativo (padrão: true)
   * @returns {Promise<object>} Presente criado
   * @returns {number} returns.id - ID único do presente criado
   * @returns {string} returns.createdAt - Data e hora de criação do presente
   */
  createGift(giftData) {
    return api.post('/gifts', giftData);
  },

  /**
   * Atualizar presente existente
   * @param {number} id - ID do presente a ser atualizado
   * @param {object} giftData - Dados atualizados do presente
   * @param {string} giftData.name - Nome do presente (máximo 100 caracteres, obrigatório)
   * @param {string} giftData.description - Descrição detalhada do presente (obrigatório)
   * @param {number} giftData.suggestedValue - Valor sugerido para o presente (obrigatório)
   * @param {string} [giftData.imageUrl] - URL da imagem do presente (opcional)
   * @param {boolean} [giftData.active] - Indica se o presente está ativo (se não informado, mantém o valor atual)
   * @returns {Promise<object>} Presente atualizado
   */
  updateGift(id, giftData) {
    return api.put(`/gifts/${id}`, giftData);
  },

  /**
   * Deletar presente
   * @param {number} id - ID do presente a ser excluído
   * @returns {Promise} Resposta vazia (204 No Content)
   */
  deleteGift(id) {
    return api.delete(`/gifts/${id}`);
  },

  /**
   * Reservar um presente do chá de panela em nome de um convidado
   * @param {number} id - ID do presente
   * @param {object} data - Dados do convidado
   * @param {string} data.name - Nome do convidado
   * @param {string} data.contact - Email ou telefone do convidado
   * @returns {Promise<object>} Presente atualizado com dados da reserva
   */
  reserveGift(id, data) {
    return api.post(`/gifts/${id}/reserve`, data);
  },

  /**
   * Buscar presentes do chá de panela reservados por um convidado (email ou telefone)
   * @param {string} contact - Email ou telefone do convidado
   * @returns {Promise<Array>} Lista de presentes reservados pelo convidado
   */
  searchTeaPartyGifts(contact) {
    return api.post('/gifts/tea-party/search', { contact });
  },

  /**
   * Verificar se um presente está disponível (não foi comprado)
   * @param {object} gift - Objeto do presente
   * @returns {boolean} True se disponível, false se já foi comprado
   */
  isGiftAvailable(gift) {
    return !gift.purchased;
  },

  /**
   * Obter status do presente
   * @param {object} gift - Objeto do presente
   * @returns {string} 'available', 'reserved', 'purchased'
   */
  getGiftStatus(gift) {
    if (gift.purchased) {
      return 'purchased';
    }
    if (gift.reserved) {
      return 'reserved';
    }
    return 'available';
  }
};
